import React from "react";
import { Link } from "react-router-dom";
import { Badge, Button, EmptyState, Text } from "@teals/ui-web";
import { PageWrapper } from "../DataSources/styles";
import {
  ComingSoonLayout,
  Crumb,
  InfoBanner,
  InstanceHeader,
  InstanceTitleRow,
  LifecycleTable,
  Panel,
  ProviderLogo,
} from "./styles";
import { INTEGRATIONS_HUB } from "./paths";

export interface ComingSoonIntegrationProps {
  name: string;
  logo: string;
  logoBg: string;
  connectLabel: string;
  subtitle: string;
  whatAgentsDo: React.ReactNode;
}

const ComingSoonIntegration: React.FC<ComingSoonIntegrationProps> = ({
  name,
  logo,
  logoBg,
  connectLabel,
  subtitle,
  whatAgentsDo,
}) => {
  return (
    <PageWrapper>
      <Crumb>
        <Link to={INTEGRATIONS_HUB}>Integrations</Link>
        {` → ${name}`}
      </Crumb>
      <InstanceHeader>
        <div>
          <InstanceTitleRow>
            <ProviderLogo $bg={logoBg}>{logo}</ProviderLogo>
            <Text variant="h2">{name}</Text>
            <Badge variant="neutral" size="sm">
              Coming soon
            </Badge>
          </InstanceTitleRow>
          <Text
            variant="body1"
            style={{
              color: "var(--text-muted)",
              fontSize: "0.875rem",
              maxWidth: "36rem",
              marginTop: "0.375rem",
              lineHeight: 1.5,
            }}
          >
            {subtitle}
          </Text>
        </div>
        <Button variant="primary" disabled title="Connect is not available yet">
          {connectLabel}
        </Button>
      </InstanceHeader>

      <InfoBanner>
        <span>ℹ️</span>
        <span>
          Connect is disabled until OAuth ships. The loop below is the product
          contract — connect, test, disconnect, and rotate.
        </span>
      </InfoBanner>

      <ComingSoonLayout>
        <div>
          <Panel>
            <h2>What agents will do</h2>
            <Text
              variant="body1"
              style={{
                fontSize: "0.875rem",
                color: "var(--text-secondary)",
                lineHeight: 1.55,
                margin: 0,
              }}
            >
              {whatAgentsDo}
            </Text>
          </Panel>
          <Panel>
            <h2>Connection lifecycle</h2>
            <LifecycleTable>
              <thead>
                <tr>
                  <th>State</th>
                  <th>User sees</th>
                  <th>Primary actions</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <Badge variant="neutral" size="sm">
                      Coming soon
                    </Badge>
                  </td>
                  <td>Value prop</td>
                  <td>Connect disabled</td>
                </tr>
                <tr>
                  <td>Connected</td>
                  <td>Site URL, scopes, last used</td>
                  <td>Test, Disconnect, Rotate</td>
                </tr>
                <tr>
                  <td>Error</td>
                  <td>Actionable message</td>
                  <td>Reconnect</td>
                </tr>
              </tbody>
            </LifecycleTable>
          </Panel>
        </div>
        <div>
          <Panel>
            <h2>Agent associations</h2>
            <EmptyState title="Associate after connect." />
            <div style={{ marginTop: "0.75rem" }}>
              <Link to="/agents">
                <Button variant="outline" size="sm">
                  Open Agents
                </Button>
              </Link>
            </div>
          </Panel>
          <Panel>
            <h2>Not this page</h2>
            <Text
              variant="body1"
              style={{
                fontSize: "0.8125rem",
                color: "var(--text-muted)",
                lineHeight: 1.5,
                margin: 0,
              }}
            >
              Cursor talking <em>to</em> Luminafactory is{" "}
              <Link to="/mcp">Lumina MCP</Link> in the avatar menu — user-bound PATs,
              not {name}.
            </Text>
          </Panel>
        </div>
      </ComingSoonLayout>
    </PageWrapper>
  );
};

export default ComingSoonIntegration;
